/**
 * 🚀 ENTERPRISE-GRADE SYNC SYSTEM
 * 
 * Event-driven sync with optimistic updates, conflict resolution, and intelligent caching
 * NO TIMERS - Uses mutation observers and event-driven architecture
 * 
 * Architecture:
 * 1. Optimistic UI updates (immediate)
 * 2. Background sync on mutations (event-driven)
 * 3. Cache invalidation on data changes
 * 4. Conflict resolution with server wins
 * 5. Offline queue with automatic retry
 */

import { indexedDBService } from '../offline/indexedDB';
import { api } from '../api';
import { updateDraftEntryField, deleteDraftEntry } from '../firebase/api';
import { toast } from 'sonner';
import { syncStatus } from './syncStatus';

type SyncEntity = 'wells' | 'entries' | 'users' | 'tasks' | 'messages' | 'notes' | 'draft_entries' | 'voice_training' | 'voice_preferences' | 'tenant_model';

interface MutationEvent {
  entity: SyncEntity;
  action: 'create' | 'update' | 'delete' | 'update_draft_field' | 'finalize_draft';
  data: any;
  optimistic: boolean;
}

class EnterpriseSyncService {
  private mutationQueue: MutationEvent[] = [];
  private isProcessing = false;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  
  // Configuration
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_RETRIES = 3;
  private readonly BATCH_SIZE = 10;

  /**
   * Initialize sync service
   */
  async initialize(): Promise<void> {
    if (!indexedDBService.isInitialized()) {
      await indexedDBService.initialize();
    }
    
    // Process any pending mutations from previous session
    await this.processPendingMutations();
    
  }

  /**
   * Subscribe to data changes for an entity
   * Returns unsubscribe function
   */
  subscribe(entity: SyncEntity, callback: (data: any) => void): () => void {
    const key = entity;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(key)?.delete(callback);
    };
  }

  /**
   * Notify all subscribers of data change
   */
  private notify(entity: SyncEntity, data: any): void {
    this.listeners.get(entity)?.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in sync subscriber for ${entity}:`, error);
      }
    });
  }

  /**
   * Get data with caching
   */
  async get<T>(entity: SyncEntity, id?: string): Promise<T | T[]> {
    const cacheKey = id ? `${entity}:${id}` : entity;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }

    // Fetch from server
    try {
      const endpoint = id ? `/${entity}/${id}` : `/${entity}`;
      const data = await api.get<T>(endpoint);
      
      // Update cache
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
        ttl: this.CACHE_TTL,
      });
      
      // Store in IndexedDB
      if (Array.isArray(data)) {
        await this.storeInIndexedDB(entity, data);
      }
      
      return data;
    } catch (error) {
      
      // Fallback to IndexedDB
      return await this.getFromIndexedDB(entity, id);
    }
  }

  /**
   * Create entity with optimistic update
   */
  async create(entity: SyncEntity, data: any): Promise<any> {
    // Generate temporary ID for optimistic update
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const optimisticData = { ...data, id: tempId, _optimistic: true };

    // 1. Immediate optimistic update (UI feels instant)
    await this.storeInIndexedDB(entity, [optimisticData]);
    this.invalidateCache(entity);
    this.notify(entity, optimisticData);

    // 2. Queue mutation for background sync
    this.queueMutation({
      entity,
      action: 'create',
      data: { ...data, _tempId: tempId },
      optimistic: true,
    });

    // 3. Process queue (non-blocking)
    this.processQueue();

    return optimisticData;
  }

  /**
   * Update entity with optimistic update
   */
  async update(entity: SyncEntity, id: string, data: any): Promise<any> {
    // 1. Immediate optimistic update
    const optimisticData = { ...data, id, _optimistic: true };
    await this.updateInIndexedDB(entity, id, optimisticData);
    this.invalidateCache(entity, id);
    this.notify(entity, optimisticData);

    // 2. Queue mutation
    this.queueMutation({
      entity,
      action: 'update',
      data: { ...data, id },
      optimistic: true,
    });

    // 3. Process queue
    this.processQueue();

    return optimisticData;
  }

  /**
   * Delete entity with optimistic update
   */
  async delete(entity: SyncEntity, id: string): Promise<void> {
    // 1. Immediate optimistic delete
    await this.deleteFromIndexedDB(entity, id);
    this.invalidateCache(entity, id);
    this.notify(entity, { id, _deleted: true });

    // 2. Queue mutation
    this.queueMutation({
      entity,
      action: 'delete',
      data: { id },
      optimistic: true,
    });

    // 3. Process queue
    this.processQueue();
  }

  /**
   * Queue a mutation for background processing
   */
  private queueMutation(mutation: MutationEvent): void {
    this.mutationQueue.push(mutation);
  }

  /**
   * Process mutation queue (event-driven, non-blocking)
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.mutationQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    syncStatus.startSync(); // Mark sync as started

    try {
      // Process in batches
      let totalSynced = 0;
      while (this.mutationQueue.length > 0) {
        const batch = this.mutationQueue.splice(0, this.BATCH_SIZE);
        
        const results = await Promise.allSettled(
          batch.map(mutation => this.processMutation(mutation))
        );

        // Count successful syncs
        totalSynced += results.filter(r => r.status === 'fulfilled').length;
      }

      // Mark sync complete
      syncStatus.completeSync(totalSynced);
    } catch (error) {
      console.error('❌ Sync queue processing failed:', error);
      syncStatus.failSync(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process a single mutation
   */
  private async processMutation(mutation: MutationEvent, retryCount = 0): Promise<void> {
    try {
      const { entity, action, data } = mutation;
      const endpoint = `/${entity}${action === 'delete' || action === 'update' ? `/${data.id}` : ''}`;

      let response;
      switch (action) {
        case 'create':
          response = await api.post(endpoint, data);
          // Replace temp ID with server ID
          if (data._tempId) {
            await this.replaceOptimisticId(entity, data._tempId, response.id);
          }
          break;
        case 'update':
          response = await api.put(endpoint, data);
          break;
        case 'delete':
          // Handle draft_entry deletion via Firebase API
          if (entity === 'draft_entries' || (data.draftId && data.wellId && data.companyId)) {
            await deleteDraftEntry(data.companyId, data.wellId, data.draftId);
            console.log(`✅ Draft deleted from Firestore: ${data.draftId}`);
          } else {
            await api.delete(endpoint);
          }
          break;
        case 'update_draft_field':
          // Sync draft field update to Firestore
          response = await this.syncDraftFieldUpdate(data);
          break;
        case 'finalize_draft':
          // Convert draft to final production entry
          response = await this.finalizeDraft(data);
          break;
      }

      // Sync succeeded - invalidate cache and notify
      this.invalidateCache(entity);
      if (response) {
        await this.storeInIndexedDB(entity, [response]);
        this.notify(entity, response);
      }

    } catch (error) {
      console.error(`❌ Sync failed for ${mutation.action} ${mutation.entity}:`, error);

      // Retry logic
      if (retryCount < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        return this.processMutation(mutation, retryCount + 1);
      } else {
        // Max retries exceeded - store for later
        await this.storeFailedMutation(mutation);
        toast.error(`Failed to sync ${mutation.entity}. Will retry later.`);
      }
    }
  }

  /**
   * Process pending mutations from previous session
   */
  private async processPendingMutations(): Promise<void> {
    if (!indexedDBService.isInitialized()) return;

    try {
      const pending = await indexedDBService.getSyncQueue();
      
      if (pending.length > 0) {
        
        for (const item of pending) {
          this.queueMutation({
            entity: item.entity as SyncEntity,
            action: item.type.toLowerCase() as 'create' | 'update' | 'delete' | 'update_draft_field' | 'finalize_draft',
            data: item.data,
            optimistic: false,
          });
        }
        
        this.processQueue();
      }
    } catch (error) {
      console.error('Failed to process pending mutations:', error);
    }
  }

  /**
   * Store failed mutation for retry
   */
  private async storeFailedMutation(mutation: MutationEvent): Promise<void> {
    if (!indexedDBService.isInitialized()) return;

    await indexedDBService.addToSyncQueue({
      type: mutation.action.toUpperCase() as 'CREATE' | 'UPDATE' | 'DELETE',
      entity: mutation.entity as any,
      data: mutation.data,
    });
  }

  /**
   * Replace optimistic ID with server ID
   */
  private async replaceOptimisticId(entity: SyncEntity, tempId: string, serverId: string): Promise<void> {
    // Implementation depends on IndexedDB structure
    // For now, just delete temp and store new
    await this.deleteFromIndexedDB(entity, tempId);
  }

  /**
   * Invalidate cache for entity
   */
  private invalidateCache(entity: SyncEntity, id?: string): void {
    if (id) {
      this.cache.delete(`${entity}:${id}`);
    } else {
      // Clear all cache entries for this entity
      Array.from(this.cache.keys())
        .filter(key => key.startsWith(`${entity}:`))
        .forEach(key => this.cache.delete(key));
    }
    this.cache.delete(entity);
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Force sync now
   */
  async syncNow(): Promise<void> {
    await this.processQueue();
    await this.processPendingMutations();
  }

  /**
   * Sync draft field update to Firestore
   */
  private async syncDraftFieldUpdate(data: any): Promise<any> {
    try {
      const { draftId, wellId, companyId, fieldName, value, userId } = data;
      
      // Build the update payload with field state
      const updateData = {
        [`fields.${fieldName}`]: value,
        lastModifiedAt: new Date().toISOString(),
        companyId, // Include for security rules
        userId, // Include for security rules
      };
      
      // Use direct Firebase API call instead of non-existent REST endpoint
      await updateDraftEntryField(companyId, wellId, draftId, updateData);
      
      console.log(`✅ Draft field synced: ${fieldName} for draft ${draftId}`);
      return { success: true, fieldName, draftId };
    } catch (error) {
      console.error('Failed to sync draft field:', error);
      throw error;
    }
  }

  /**
   * Finalize draft - convert to production entry
   * Note: The main production entry creation happens in DataEntry.tsx via upsertWellEntry
   * This method handles the draft cleanup when triggered from sync queue
   */
  private async finalizeDraft(data: any): Promise<any> {
    try {
      const { draftId, wellId, companyId, productionEntry } = data;
      
      // Delete draft from Firestore using Firebase API
      await deleteDraftEntry(companyId, wellId, draftId);
      
      console.log(`✅ Draft finalized and deleted: ${draftId}`);
      return { success: true, draftId };
    } catch (error) {
      console.error('Failed to finalize draft:', error);
      throw error;
    }
  }

  // ============= IndexedDB Helpers =============

  private async storeInIndexedDB(entity: SyncEntity, data: any[]): Promise<void> {
    if (!indexedDBService.isInitialized()) return;

    switch (entity) {
      case 'wells':
        await indexedDBService.saveWells(data);
        break;
      case 'entries':
        await indexedDBService.saveEntries(data);
        break;
      case 'tasks':
        await indexedDBService.saveTasks(data);
        break;
      case 'messages':
        await indexedDBService.saveMessages(data);
        break;
      case 'notes':
        await indexedDBService.saveNotes(data);
        break;
      case 'draft_entries':
        // Save draft entries to IndexedDB
        for (const draft of data) {
          await indexedDBService.saveDraftEntry(draft);
        }
        break;
      case 'voice_training':
        // Save voice training data
        for (const training of data) {
          await indexedDBService.saveFieldTraining(training);
        }
        break;
      case 'voice_preferences':
        // Save voice field preferences
        for (const prefs of data) {
          await indexedDBService.saveFieldPreferences(prefs);
        }
        break;
      case 'tenant_model':
        // Save tenant voice model (singleton)
        if (data.length > 0) {
          await indexedDBService.saveTenantModel(data[0]);
        }
        break;
    }
  }

  private async updateInIndexedDB(entity: SyncEntity, id: string, data: any): Promise<void> {
    // Get existing, merge, and save
    const existing = await this.getFromIndexedDB(entity, id);
    if (existing) {
      const updated = Array.isArray(existing) ? existing : { ...existing, ...data };
      await this.storeInIndexedDB(entity, Array.isArray(updated) ? updated : [updated]);
    }
  }

  private async deleteFromIndexedDB(entity: SyncEntity, id: string): Promise<void> {
    if (!indexedDBService.isInitialized()) return;

    switch (entity) {
      case 'wells':
        await indexedDBService.deleteWell(id);
        break;
      case 'entries':
        await indexedDBService.deleteEntry(id);
        break;
      case 'tasks':
        await indexedDBService.deleteTask(id);
        break;
      case 'messages':
        await indexedDBService.deleteMessage(id);
        break;
      case 'notes':
        await indexedDBService.deleteNote(id);
        break;
      case 'draft_entries':
        await indexedDBService.deleteDraftEntry(id);
        break;
      case 'voice_training':
        // Note: Voice training data is typically immutable, but allow deletion for privacy
        // Implementation would need a deleteFieldTraining method in indexedDBService
        break;
      case 'voice_preferences':
        // Deletion handled by replacing preferences, not deleting
        break;
      case 'tenant_model':
        // Tenant models shouldn't be deleted individually
        break;
    }
  }

  private async getFromIndexedDB(entity: SyncEntity, id?: string): Promise<any> {
    if (!indexedDBService.isInitialized()) return id ? null : [];

    let data: any;
    switch (entity) {
      case 'wells':
        data = await indexedDBService.getWells();
        break;
      case 'entries':
        data = await indexedDBService.getEntries();
        break;
      case 'tasks':
        data = await indexedDBService.getTasks();
        break;
      case 'messages':
        data = await indexedDBService.getMessages();
        break;
      case 'notes':
        data = await indexedDBService.getNotes();
        break;
      case 'draft_entries':
        // Get all drafts or specific draft by ID
        if (id) {
          return await indexedDBService.getDraftEntryById(id);
        }
        return await indexedDBService.getAllDraftEntries('current-user');
      case 'voice_training':
        // Get voice training data (requires userId context)
        if (id) {
          // id format: "userId_fieldName" for context
          const [userId, fieldName] = id.split('_');
          return await indexedDBService.getFieldTrainingData(userId, fieldName);
        }
        return []; // Cannot get all without context
      case 'voice_preferences':
        // Get voice field preferences
        if (id) {
          const [userId, fieldName] = id.split('_');
          return await indexedDBService.getFieldPreferences(userId, fieldName);
        }
        return []; // Cannot get all without context
      case 'tenant_model':
        // Get tenant voice model
        if (id) {
          return await indexedDBService.getTenantModel(id);
        }
        return [];
      default:
        return id ? null : [];
    }

    if (id) {
      return data.find((item: any) => item.id === id);
    }
    return data;
  }
}

// Singleton instance
export const enterpriseSync = new EnterpriseSyncService();

// Initialize on import (non-blocking)
enterpriseSync.initialize().catch(console.error);

export default enterpriseSync;
